import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";

import { AuthMenu } from "@/components/AuthMenu";
import { useCurrentUser } from "@/hooks/useAuth";
import { useStats } from "@/hooks/useStats";
import {
  type AdminTaskState,
  type PrefetchPlan,
  useAdminTasks,
  usePrefetchPlan,
  useStartImport,
  useStartPrefetch,
} from "@/hooks/useAdminTasks";

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function formatNumber(n: number): string {
  return n.toLocaleString();
}

export function AdminStatsPage() {
  const navigate = useNavigate();
  const { data: user, isLoading: authLoading } = useCurrentUser();
  const { data, isLoading, error, dataUpdatedAt, refetch } = useStats();

  useEffect(() => {
    if (authLoading) return;
    if (!user || !user.is_admin) {
      navigate("/", { replace: true });
    }
  }, [authLoading, user, navigate]);

  if (authLoading || !user || !user.is_admin) return null;

  return (
    <>
      <Helmet>
        <title>Stats — Admin</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="min-h-screen bg-surface-0 flex flex-col">
        <div className="sticky top-0 z-30 bg-surface-0/95 backdrop-blur border-b border-surface-2 px-4 py-3">
          <div className="max-w-5xl mx-auto flex flex-wrap items-center gap-x-3 gap-y-2">
            <Link
              to="/"
              className="text-sm text-zinc-400 hover:text-accent-light min-h-[40px] inline-flex items-center"
            >
              ← Search
            </Link>
            <h1 className="text-sm font-semibold">Stats</h1>
            <span className="text-[10px] uppercase tracking-wider text-accent-light">
              admin
            </span>
            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={() => refetch()}
                className="text-xs text-zinc-400 hover:text-accent-light min-h-[40px] px-2 inline-flex items-center"
                title="Refresh stats now"
              >
                ↻ refresh
              </button>
              <AuthMenu />
            </div>
          </div>
        </div>

        <div className="flex-1 px-4 py-6 max-w-5xl mx-auto w-full space-y-6">
          {error && (
            <div className="bg-red-900/30 border border-red-800 text-red-200 text-sm rounded p-3">
              Failed to load stats: {(error as Error).message}
            </div>
          )}

          {isLoading || !data ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="bg-surface-2 h-24 rounded animate-pulse" />
              ))}
            </div>
          ) : (
            <>
              <Section title="Index">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Tile label="Posts" value={formatNumber(data.db.posts_total)} />
                  <Tile label="Tags" value={formatNumber(data.db.tags_total)} />
                  <Tile label="Aliases" value={formatNumber(data.db.aliases_total)} />
                  <Tile
                    label="Implications"
                    value={formatNumber(data.db.implications_total)}
                  />
                </div>
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <SubTile
                    label="blocked posts"
                    value={formatNumber(data.db.posts_blocked)}
                    hint="hard-safety filtered at ingest"
                  />
                  <SubTile
                    label="deleted posts"
                    value={formatNumber(data.db.posts_deleted)}
                    hint="taken down upstream"
                  />
                  {Object.entries(data.db.posts_animated_by_ext).map(([ext, n]) => (
                    <SubTile
                      key={ext}
                      label={`.${ext}`}
                      value={formatNumber(n)}
                      hint="visible posts"
                    />
                  ))}
                </div>
              </Section>

              <Section title="Sync">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Tile
                    label="Last e621 dump"
                    value={data.sync.last_dump_date ?? "—"}
                  />
                  <Tile
                    label="Last full run"
                    value={
                      data.sync.last_full_run
                        ? new Date(data.sync.last_full_run).toLocaleString()
                        : "—"
                    }
                  />
                  <Tile
                    label="Auto-sync"
                    value={data.sync.auto_sync_enabled ? "scheduled" : "manual only"}
                    hint={
                      data.sync.auto_sync_enabled
                        ? data.sync.next_scheduled_at ?? undefined
                        : "task import / kubectl exec on cluster"
                    }
                  />
                </div>
              </Section>

              <Section title="Media cache & disk">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Tile
                    label="Cached files"
                    value={formatNumber(data.disk.total_files)}
                  />
                  <Tile label="On disk" value={formatBytes(data.disk.total_bytes)} />
                  <Tile
                    label="Hit ratio"
                    value={
                      data.media_cache.hit_ratio === null
                        ? "—"
                        : `${(data.media_cache.hit_ratio * 100).toFixed(1)}%`
                    }
                    hint={`${formatNumber(data.media_cache.hits)} hits / ${formatNumber(
                      data.media_cache.misses,
                    )} miss`}
                  />
                  <Tile
                    label="Geo enabled"
                    value={data.geo_enabled ? "yes" : "no"}
                    hint={data.geo_enabled ? "GeoLite2 loaded" : "no .mmdb mounted"}
                  />
                </div>
                {Object.keys(data.disk.breakdown).length > 0 && (
                  <div className="mt-3 overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="text-[10px] uppercase tracking-wider text-zinc-500 text-left border-b border-surface-3">
                          <th className="py-2 pr-4">Bucket</th>
                          <th className="py-2 pr-4 text-right">Files</th>
                          <th className="py-2 pr-4 text-right">Size</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(data.disk.breakdown)
                          .sort((a, b) => b[1].bytes - a[1].bytes)
                          .map(([bucket, b]) => (
                            <tr
                              key={bucket}
                              className="border-b border-surface-2/50"
                            >
                              <td className="py-1.5 pr-4 font-mono text-accent-light">
                                {bucket}
                              </td>
                              <td className="py-1.5 pr-4 text-right tabular-nums">
                                {formatNumber(b.files)}
                              </td>
                              <td className="py-1.5 pr-4 text-right tabular-nums">
                                {formatBytes(b.bytes)}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <p className="text-[10px] text-zinc-600 mt-2 font-mono">
                  {data.disk.downloads_dir}
                </p>
              </Section>

              <Section title="Download jobs">
                {Object.keys(data.downloads.jobs_by_status).length === 0 ? (
                  <Empty>No jobs have run yet.</Empty>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {(["queued", "downloading", "transcoding", "done", "failed"] as const).map(
                      (status) => (
                        <Tile
                          key={status}
                          label={status}
                          value={formatNumber(
                            data.downloads.jobs_by_status[status] ?? 0,
                          )}
                        />
                      ),
                    )}
                  </div>
                )}
                {Object.keys(data.downloads.jobs_by_preset_done).length > 0 && (
                  <div className="mt-3">
                    <h3 className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
                      Completed by preset
                    </h3>
                    <ul className="space-y-1 text-sm">
                      {Object.entries(data.downloads.jobs_by_preset_done).map(
                        ([preset, n]) => (
                          <li
                            key={preset}
                            className="flex justify-between font-mono"
                          >
                            <span className="text-accent-light">{preset}</span>
                            <span className="tabular-nums text-zinc-400">
                              {formatNumber(n)}
                            </span>
                          </li>
                        ),
                      )}
                    </ul>
                  </div>
                )}
              </Section>

              <Section title="Maintenance">
                <MaintenancePanel />
              </Section>

              <p className="text-[10px] text-zinc-600 text-right">
                Last updated: {new Date(dataUpdatedAt).toLocaleTimeString()}
              </p>
            </>
          )}
        </div>
      </div>
    </>
  );
}

function Tile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="bg-surface-1 border border-surface-3 rounded p-4">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">
        {label}
      </div>
      <div className="text-2xl font-semibold mt-1 tabular-nums truncate">
        {value}
      </div>
      {hint && <div className="text-[10px] text-zinc-600 mt-1 truncate">{hint}</div>}
    </div>
  );
}

function SubTile({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="bg-surface-1/50 border border-surface-3/50 rounded p-2.5">
      <div className="text-[10px] uppercase tracking-wider text-zinc-600">
        {label}
      </div>
      <div className="text-sm font-semibold mt-0.5 tabular-nums">{value}</div>
      {hint && <div className="text-[10px] text-zinc-600 mt-0.5">{hint}</div>}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-xs uppercase tracking-wider text-zinc-500 mb-2">
        {title}
      </h2>
      {children}
    </section>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-surface-1 border border-surface-3 rounded p-6 text-center text-sm text-zinc-500">
      {children}
    </div>
  );
}

// ── Maintenance: import + prefetch buttons ──────────────────────────────────

const DEFAULT_PREFETCH_PARAMS = { top_queries: 20, top_tags: 20, days: 7 };

function MaintenancePanel() {
  const { data: tasks = [] } = useAdminTasks();
  const byKind = Object.fromEntries(tasks.map((t) => [t.kind, t])) as Record<
    string,
    AdminTaskState | undefined
  >;
  const importTask = byKind["import"];
  const prefetchTask = byKind["prefetch"];

  const { mutate: startImport, isPending: importStarting } = useStartImport();
  const { mutate: planPrefetch, isPending: planning, data: plan, reset: resetPlan } =
    usePrefetchPlan();
  const { mutate: executePrefetch, isPending: executing } = useStartPrefetch();

  const [importConfirming, setImportConfirming] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  function doImport() {
    setErrorMsg(null);
    startImport(true, {
      onSuccess: () => setImportConfirming(false),
      onError: (e) => setErrorMsg((e as Error).message),
    });
  }

  function doPlan() {
    setErrorMsg(null);
    planPrefetch(DEFAULT_PREFETCH_PARAMS, {
      onError: (e) => setErrorMsg((e as Error).message),
    });
  }

  function doExecute() {
    setErrorMsg(null);
    executePrefetch(plan?.params ?? DEFAULT_PREFETCH_PARAMS, {
      onSuccess: () => resetPlan(),
      onError: (e) => setErrorMsg((e as Error).message),
    });
  }

  return (
    <div className="space-y-3">
      {errorMsg && (
        <div className="bg-red-900/30 border border-red-800 text-red-200 text-xs rounded p-2">
          {errorMsg}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* ── Refresh index ───────────────────────────────────────────── */}
        <div className="bg-surface-1 border border-surface-3 rounded p-4 space-y-2">
          <div className="flex items-baseline justify-between">
            <h3 className="font-semibold text-sm">Refresh index</h3>
            <StatusPill task={importTask} />
          </div>
          <p className="text-xs text-zinc-500 leading-relaxed">
            Re-fetch the latest e621 db_export (~100 MB compressed) and
            re-ingest tags / aliases / implications / animated posts.
            Skipped automatically when the local index already matches the
            remote dump date.
          </p>
          {importTask?.status === "running" && (
            <RunningBar task={importTask} />
          )}
          {importTask?.status === "succeeded" && (
            <p className="text-xs text-green-400">{importTask.message}</p>
          )}
          {importTask?.status === "failed" && (
            <p className="text-xs text-red-400 break-words">
              {importTask.error}
            </p>
          )}

          {!importConfirming ? (
            <button
              onClick={() => setImportConfirming(true)}
              disabled={importTask?.status === "running"}
              className="text-xs px-3 py-2 min-h-[36px] rounded bg-surface-2 hover:bg-surface-3 text-zinc-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Refresh index…
            </button>
          ) : (
            <div className="space-y-2 text-xs">
              <p className="text-amber-400">
                Run a forced re-ingest now? Takes 5–10 minutes; the app
                stays available throughout.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={doImport}
                  disabled={importStarting}
                  className="px-3 py-2 min-h-[36px] rounded bg-accent hover:bg-accent-hover text-white font-semibold disabled:opacity-50"
                >
                  {importStarting ? "Starting…" : "Yes, refresh"}
                </button>
                <button
                  onClick={() => setImportConfirming(false)}
                  className="px-3 py-2 min-h-[36px] rounded bg-surface-2 hover:bg-surface-3 text-zinc-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Prefetch popular ────────────────────────────────────────── */}
        <div className="bg-surface-1 border border-surface-3 rounded p-4 space-y-2">
          <div className="flex items-baseline justify-between">
            <h3 className="font-semibold text-sm">Prefetch popular</h3>
            <StatusPill task={prefetchTask} />
          </div>
          <p className="text-xs text-zinc-500 leading-relaxed">
            Warm the preview cache for the top {DEFAULT_PREFETCH_PARAMS.top_queries}{" "}
            queries and top {DEFAULT_PREFETCH_PARAMS.top_tags} tags from the
            last {DEFAULT_PREFETCH_PARAMS.days} days of analytics. Plan
            first, then confirm.
          </p>
          {prefetchTask?.status === "running" && (
            <RunningBar task={prefetchTask} />
          )}
          {prefetchTask?.status === "succeeded" && (
            <p className="text-xs text-green-400">{prefetchTask.message}</p>
          )}
          {prefetchTask?.status === "failed" && (
            <p className="text-xs text-red-400 break-words">
              {prefetchTask.error}
            </p>
          )}

          {plan ? (
            <PrefetchPlanPanel
              plan={plan}
              executing={executing}
              onConfirm={doExecute}
              onCancel={() => resetPlan()}
            />
          ) : (
            <button
              onClick={doPlan}
              disabled={planning || prefetchTask?.status === "running"}
              className="text-xs px-3 py-2 min-h-[36px] rounded bg-surface-2 hover:bg-surface-3 text-zinc-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {planning ? "Calculating…" : "Plan prefetch…"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusPill({ task }: { task: AdminTaskState | undefined }) {
  if (!task || task.status === "idle") return null;
  const color =
    task.status === "running"
      ? "bg-yellow-900/40 text-yellow-300"
      : task.status === "succeeded"
        ? "bg-green-900/40 text-green-300"
        : "bg-red-900/40 text-red-300";
  return (
    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${color}`}>
      {task.status}
    </span>
  );
}

function RunningBar({ task }: { task: AdminTaskState }) {
  return (
    <div className="space-y-1">
      <div className="h-1 bg-surface-3 rounded-full overflow-hidden">
        <div
          className="h-full bg-accent transition-all"
          style={{ width: `${task.progress}%` }}
        />
      </div>
      <p className="text-[10px] text-zinc-500 truncate">{task.message}</p>
    </div>
  );
}

function PrefetchPlanPanel({
  plan,
  executing,
  onConfirm,
  onCancel,
}: {
  plan: PrefetchPlan;
  executing: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const sampledQ = plan.sampled_queries.slice(0, 5);
  const sampledT = plan.sampled_tags.slice(0, 5);
  return (
    <div className="space-y-2 bg-surface-2/50 rounded p-3 text-xs">
      <div className="grid grid-cols-3 gap-2">
        <PlanStat label="unique posts" value={plan.total_unique_posts} />
        <PlanStat label="to fetch" value={plan.previews_to_fetch} highlight />
        <PlanStat label="already cached" value={plan.previews_already_cached} />
      </div>
      <p className="text-zinc-500 leading-relaxed">
        Estimated ~{formatBytes(plan.estimated_bytes)}, capped at 4
        concurrent fetches against e621's CDN.
      </p>
      {(sampledQ.length > 0 || sampledT.length > 0) && (
        <p className="text-zinc-500">
          {sampledQ.length > 0 && (
            <>
              <span className="text-zinc-400">Queries:</span>{" "}
              <span className="font-mono text-accent-light">
                {sampledQ.join(", ")}
                {plan.sampled_queries.length > sampledQ.length && " …"}
              </span>
              <br />
            </>
          )}
          {sampledT.length > 0 && (
            <>
              <span className="text-zinc-400">Tags:</span>{" "}
              <span className="font-mono text-accent-light">
                {sampledT.join(", ")}
                {plan.sampled_tags.length > sampledT.length && " …"}
              </span>
            </>
          )}
        </p>
      )}
      {plan.previews_to_fetch === 0 ? (
        <div className="flex gap-2">
          <button
            disabled
            className="px-3 py-2 min-h-[36px] rounded bg-surface-2 text-zinc-500 cursor-not-allowed"
          >
            Nothing to fetch
          </button>
          <button
            onClick={onCancel}
            className="px-3 py-2 min-h-[36px] rounded bg-surface-2 hover:bg-surface-3 text-zinc-300"
          >
            Dismiss
          </button>
        </div>
      ) : (
        <div className="flex gap-2">
          <button
            onClick={onConfirm}
            disabled={executing}
            className="px-3 py-2 min-h-[36px] rounded bg-accent hover:bg-accent-hover text-white font-semibold disabled:opacity-50"
          >
            {executing
              ? "Starting…"
              : `Yes, fetch ${plan.previews_to_fetch} previews`}
          </button>
          <button
            onClick={onCancel}
            className="px-3 py-2 min-h-[36px] rounded bg-surface-2 hover:bg-surface-3 text-zinc-300"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

function PlanStat({
  label,
  value,
  highlight = false,
}: {
  label: string;
  value: number;
  highlight?: boolean;
}) {
  return (
    <div
      className={`rounded p-2 ${
        highlight
          ? "bg-accent/15 text-accent-light"
          : "bg-surface-1 text-zinc-300"
      }`}
    >
      <div className="text-[9px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-base font-semibold tabular-nums">{value.toLocaleString()}</div>
    </div>
  );
}
